import useSuccessToast from "@configs/react-toastify/snackbar";
import { TOAST } from "@constants/index";
import AddVerificationReportForm from "@components/UI/AddVerificationReportForm";
import Breadcrumb from "@components/UI/Breadcrumb";
import queryKeys from "@configs/react-query/query-key";
import { useMutationData } from "@hooks/useMutationData";
import { useTheme } from "@hooks/useTheme";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ToastConstant from "@configs/react-toastify/snackbar";
import { VERIFICATION_TYPE } from "@constants";
import { OutlineButton, PrimaryButton } from "@components/Buttons";
import { useTranslation } from "react-i18next";
import { updateDataInIndexedDb, STORES } from "@utils/indexedDb";
import { resolveVerificationToolCdForReport } from "@utils/common";

const AddVerificationReport = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { colors } = useTheme();
  const [loading, setLoading] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const formRef = useRef(null);

  const breadcrumbItems = [
    { label: t("project.verification-report.verification-report-list"), href: "/verification-report" },
    { label: t("project.verification-report.add-verif-report"), href: "/verification-report/new" },
  ];

  // Check form validity whenever form data changes
  useEffect(() => {
    const checkFormValidity = () => {
      if (formRef.current) {
        const formData = formRef.current.getFormData();
        const isValid =
          formData.verificationReportName.trim() !== "" &&
          formData.noticeInfoCompletionDate &&
          formData.verificationFile !== null;
        setIsFormValid(isValid);
      }
    };

    // Check immediately
    checkFormValidity();

    // Set up interval to check periodically (since we can't directly listen to form changes)
    const interval = setInterval(checkFormValidity, 100);

    return () => clearInterval(interval);
  }, []);

  // API call for creating verification report
  const createVerificationReport = useMutationData(
    queryKeys.addVerificationReport,
    {
      method: "POST",
    },
    {
      onSuccess: async (response) => {
        await updateDataInIndexedDb(STORES.VERIFICATION_REPORTS, response, response.verificationReportSeq);
        useSuccessToast(TOAST.success, t("project.verification-report.verification-report-add-success"));
        navigate("/verification-report");
      },
      onError: (error) => {
        console.error("Error during create verification report:", error);
        ToastConstant(TOAST.error, t("common.failed-to-create-verification-report"));
        setLoading(false);
      },
    },
  );

  const handleCancel = () => {
    navigate("/verification-report");
  };

  const transformComponentData = (data) => {
    if (!data || !Array.isArray(data)) return [];
    return data.map((item) => ({
      ...item,
      componentName: item.component || item.componentName,
      licenseName: item.componentLicense || item.licenseName ||"" ,
    }));
  };

  const handleSave = async () => {
    if (formRef.current) {
      const formData = formRef.current.getFormData();

      // Validate form data
      if (!formData.verificationReportName.trim()) {
        ToastConstant(TOAST.error, t("common.please-enter-verification-report-name"));
        return;
      }

      if (!formData.noticeInfoCompletionDate) {
        ToastConstant(TOAST.error, t("common.please-select-completion-date"));
        return;
      }

      if (formData.verificationFile === null) {
        ToastConstant(TOAST.error, t("common.please-select-verification-project-version"));
        return;
      }
      const selectedFile = formData.verificationFile;

      // Extract projectId and verificationVersion from the selected file
      const projectId = selectedFile.projectId;
      const verificationVersion = selectedFile.verificationVersion;
      const scanComponentData = formData.scanComponentData;
      const reportFileName = selectedFile.realFileName;

      if (!projectId || !verificationVersion) {
        ToastConstant(TOAST.error, t("common.invalid-verification-project-version-data"));
        return;
      }

      const verificationToolCd = resolveVerificationToolCdForReport(selectedFile);

      setLoading(true);

      // Create the payload with the correct structure
      const requestData = {
        developmentDeptName: "",
        expectedNoticeInfoEntryCompletionDt: formData.noticeInfoCompletionDate || "",
        projectId: projectId,
        useArea: "",
        verificationReportDetails: transformComponentData(scanComponentData),
        verificationReportId: "",
        verificationReportName: formData.verificationReportName,
        verificationReportProjects: [{
          projectId: projectId,
          verificationVersion: verificationVersion
        }],
        verificationReportResultCd: "",
        verificationReportStatusCd: "ON_WRITE",
        verificationReportVersion: "",
        verificationToolCd: verificationToolCd,
        verificationVersion: verificationVersion,
        reportFileName: reportFileName,
      };

      await createVerificationReport.mutateAsync(requestData);
    }
  };

  return (
    <div data-testid="add-verification-report-page">
      {/* Header */}
      <div className={`${colors.bgSecondary} ${colors.borderPrimary}`} data-testid="add-verification-report-header">
        <div className="flex items-center justify-between">
          <div className="items-center space-x-4">
            <Breadcrumb data-testid="add-verification-report-breadcrumb" items={breadcrumbItems} />
            <h1
              className={`${colors.textPrimary} text-2xl font-bold uppercase`}
              data-testid="add-verification-report-title"
            >
              {t("project.verification-report.add-verif-report")}
            </h1>
          </div>
          <div className="flex items-center space-x-3" data-testid="add-verification-report-actions">
            <OutlineButton data-testid="add-verification-report-cancel-button" onClick={handleCancel}>
              {t("common.cancel-button")}
            </OutlineButton>
            <PrimaryButton
              data-testid="add-verification-report-save-button"
              onClick={handleSave}
              disabled={!isFormValid || loading}
            >
              {t("common.save-button")}
            </PrimaryButton>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="mt-5" data-testid="add-verification-report-form">
        <AddVerificationReportForm ref={formRef} />
      </div>
    </div>
  );
};

export default AddVerificationReport;
